import { Injectable, Type } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { L94Rfil94 } from '@/core/models/models/pages/l94rfil94';

@Injectable({ providedIn: 'root' })
export class UtilsService {
    private isMobileDevice: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private isCellDevice: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private isBigScreen: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(
        private dialogService: DialogService,
        private messageService: MessageService
    ) {
        // media query per versione "mobile": da usare metodi --> isMobile$ / isCurrentScreenMobile
        const mediaQueryList = window.matchMedia('only screen and (max-width: 1023px)');
        this.isMobileDevice.next(mediaQueryList.matches);
        mediaQueryList.addEventListener('change', (event) => this.isMobileDevice.next(event.matches));

        const mediaQueryCell = window.matchMedia('only screen and (max-width: 450px)');
        this.isCellDevice.next(mediaQueryCell.matches);
        mediaQueryCell.addEventListener('change', (event) => this.isCellDevice.next(event.matches));

        const mediaQueryBigScreen = window.matchMedia('only screen and (min-width: 1200px)');
        this.isBigScreen.next(mediaQueryBigScreen.matches);
        mediaQueryBigScreen.addEventListener('change', (event) => this.isBigScreen.next(event.matches));
    }

    // ###########################################  GESTIONE RESPONSIVE ############################################################

    get isMobile$(): Observable<boolean> {
        return this.isMobileDevice.asObservable();
    }

    isCurrentScreenMobile(): boolean {
        return this.isMobileDevice.getValue();
    }

    isCell(): boolean {
        return this.isCellDevice.getValue();
    }

    isPCScreen$(): Observable<boolean> {
        return this.isBigScreen.asObservable();
    }

    isPCScreen(): boolean {
        return this.isBigScreen.getValue();
    }

    // ###########################################  GESTIONE RESPONSIVE ############################################################

    // ###########################################  GESTIONE POPUP/TOAST ############################################################

    smartConcat(data: string[], separator = ' '): string {
        if (data?.length > 1) {
            let result = '';
            data.forEach((d, index) => {
                const trimmedVal: string = this.checkNullAndTrim(d);
                const isLastElement: boolean = index === data.length - 1;
                const theresOthers: boolean = !!data.slice(index + 1, data.length).find((val) => !!val);
                result += trimmedVal.length ? trimmedVal + (!isLastElement && theresOthers ? separator : '') : '';
            });
            return result;
        } else if (data?.length === 1) {
            return this.checkNullAndTrim(data[0]);
        } else {
            return '';
        }
    }

    /**
     * Ritorna stringa vuota se il campo è null/undefined + esegue il trim se NON null/undefined
     */
    checkNullAndTrim(input: any, substitute?: string): string {
        return !!input ? ('' + input).trim() : substitute ? substitute : '';
    }

    /**
     * Ritorna null se il campo è = stringa vuota (x set inversi)
     */
    checkEmpty(input: string) {
        return input && input.trim() === '' ? null : input;
    }

    /**
     * true valore non definito o stringa vuota, false altrimenti
     */
    checkNullOrEmpty(input: any): boolean {
        return !input || !(('' + input)?.trim()).length;
    }

    isNumber(numValue: string): boolean {
        return this.checkValid(numValue) && !isNaN(Number(numValue));
    }

    checkValid(value: any): boolean {
        return value !== null && value !== undefined;
    }

    checkAtLeastOneValue(obj: any, ...fieldsToExclude: string[]): boolean {
        return obj
            ? Object.keys(obj)
                  .filter((key) => !fieldsToExclude.includes(key))
                  .some((key) => this.checkValid(obj[key]))
            : false;
    }

    checkNotValidFields(obj: any, fields: string[]): boolean {
        return obj
            ? Object.keys(obj)
                  .filter((key) => fields.includes(key))
                  .some((key) => !this.checkValid(obj[key]))
            : false;
    }

    checkValidStrong(value: any): boolean {
        return value !== null && value !== undefined && value !== 0;
    }

    checkValidArray(value: any[], substitute = []): any[] {
        return value !== null && value !== undefined ? value : substitute;
    }

    checkValidAndNotEmpty(value: any): boolean {
        return value !== null && value !== undefined && value !== '';
    }

    checkValidAndNotEmptyStrong(value: any): boolean {
        return value !== null && value !== undefined && value !== 0 && value !== '';
    }

    fireConfirmationToast(title?: string, text?: string, duration = 2500) {
        this.messageService.add({ severity: 'success', summary: title, detail: text, life: duration, closable: true });
    }

    fireWarningToast(title?: string, text?: string, duration = 2500) {
        this.messageService.add({ severity: 'warn', summary: title, detail: text, life: duration, closable: true });
    }

    openDialog(contentComponent: Type<any>, config: DynamicDialogConfig): DynamicDialogRef {
        return this.dialogService.open(contentComponent, config);
    }

    arrayMove(array: any[], fromIndex: number, toIndex: number) {
        const element = array[fromIndex];
        array.splice(fromIndex, 1);
        array.splice(toIndex, 0, element);
    }

    capitalize(text: string) {
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    /**
     * Ritorna l'array in input ordinato per campo in input di TIPO STRINGA (reverse=true per ordinamento inverso)
     */
    public sortArrayByStringField(array: any[], fieldName: string, reverse?: boolean): any[] {
        if (array) {
            return !reverse
                ? array.sort((f1, f2) => (fieldName.split('.').length > 1 ? f1[fieldName.split('.')[0]][fieldName.split('.')[1]]?.localeCompare(f2[fieldName.split('.')[0]][fieldName.split('.')[1]]) : f1[fieldName]?.localeCompare(f2[fieldName])))
                : array.sort((f1, f2) => (fieldName.split('.').length > 1 ? f2[fieldName.split('.')[0]][fieldName.split('.')[1]]?.localeCompare(f1[fieldName.split('.')[0]][fieldName.split('.')[1]]) : f2[fieldName]?.localeCompare(f1[fieldName])));
        }
        return [];
    }

    getFormControl(form: FormGroup, controlName: string): FormControl {
        return form.controls[controlName] as FormControl;
    }

    /* Scorre la pagina fino all'elemento specifico identificato dal suo ID */
    scrollTo(id: string, block?: ScrollLogicalPosition) {
        setTimeout(() => {
            const element = document.getElementById(id);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: block ? block : 'center' });
            }
        }, 100);
    }

    getInputRangeClass(field: string, form: FormGroup, normativa?: any): string {
        if (!form) return '';
        const control = form.get(field);
        if (!control) return '';
        const rawValue = control.value;
        if (rawValue === null || rawValue === undefined || rawValue === '') {
            return '';
        }
        
        const value = Number(rawValue);

        const baseField = field.replace(/^(ramo1_|ramo2_|cuore_|telaio1_|telaio2_|telaio3_|telaio4_)/, '') as keyof L94Rfil94;

        const base = Number(normativa?.[baseField]);
        const min = Number((normativa as any)?.[`min${baseField.charAt(0).toUpperCase() + baseField.slice(1)}`]);
        const plus = Number((normativa as any)?.[`plus${baseField.charAt(0).toUpperCase() + baseField.slice(1)}`]);

        if (isNaN(base)) {
            return '';
        }

        const lower = base - (isNaN(min) ? 0 : min);
        const upper = base + (isNaN(plus) ? 0 : plus);

        if (value >= lower && value <= upper) {
            return 'bg-green-200 border-green-400';
        } else {
            return 'bg-red-200 border-red-400';
        }
    }

    private stripPrefix(field: string): string {
        return field.replace(/^(ramo1_|ramo2_|cuore_|telaio1_|telaio2_|telaio3_|telaio4_)/, '');
    }

    private getOpAndTargetFor(field: string, normativa?: any): { op?: string; target?: number } {
        if (!normativa) return {};
        const base = this.stripPrefix(field);
        const opKey = ('op' + base.toUpperCase()) as keyof L94Rfil94;
        const valKey = base as keyof L94Rfil94;
        const op = (normativa as any)?.[opKey] as string | undefined;
        const target = Number((normativa as any)?.[valKey]);
        return { op, target: isNaN(target) ? undefined : target };
    }

    private evalRule(value: number, op?: string, target?: number): boolean {
        if (op == null || target == null || isNaN(value)) return true;
        const nrm = op.toString().trim().toLowerCase();

        if (nrm === '≤' || nrm === '<=') return value <= target;
        if (nrm === '≥' || nrm === '>=') return value >= target;
        if (nrm === '<') return value < target;
        if (nrm === '>') return value > target;
        if (nrm === '=' || nrm === '==') return value === target;

        if (nrm.startsWith('>=')) return value >= target;
        if (nrm.startsWith('<=')) return value <= target;

        return true;
    }

    getRuleClass(field: string, form: FormGroup, normativa?: any): string {
        if (!form) return '';
        const control = form.get(field);
        if (!control) return '';

        const raw = control.value;
        if (raw === null || raw === undefined || raw === '') {
            if (control.hasError('ruleError')) {
                const { ruleError, ...rest } = control.errors || {};
                control.setErrors(Object.keys(rest).length ? rest : null);
            }
            return '';
        }

        const value = Number(raw);
        const { op, target } = this.getOpAndTargetFor(field, normativa);
        const ok = this.evalRule(value, op, target);

        if (control.hasError('ruleError')) {
            const { ruleError, ...rest } = control.errors || {};
            control.setErrors(Object.keys(rest).length ? rest : null);
        }

        return ok ? 'bg-green-200 border-green-400' : 'bg-red-200 border-red-400';
    }

    pref(prefix: string, keys: string[]): string[] {
        return keys.map((k) => prefix + k);
    }
}
